import Component from 'vue-class-component';
import PageBase from '@/pages/page-base';
import MapFeaturesService from '@/services/map-features.service';
import Feature from '@/interfaces/geo/feature.interface';
import MapObject from '@/interfaces/entities/map-object.interface';
import AppMapFilter from '@/components/map-filter/map-filter.vue';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import Filter from '@/library/filter/filter.interface';
import Filters from '@/library/filter/filters';
import {MediaQueryService} from '@/services/media-query.service';
import ApiService from '@/services/api.service';
import MapService from '@/services/map.service';
import {MapObjectInRoute} from '@/library/route/map-object-in-route';
import {MapRouteInRoute} from '@/library/route/map-route-in-route';
import MapRoute from '@/interfaces/entities/map-route.interface';
import ObjectReportService from '@/services/object-report.service';
import LayerVisibility from '@/interfaces/geo/layer-visibility.interface';
import MapLayersService from '@/services/map-layers.service';
import {CustomObservable} from '@/library/observable/custom-observable';
import AppMapInfoContainer from '@/components/map-info-container/map-info-container.vue';
import {MapObjectType} from '@/enums/map-object-type';

@Component({})
export default class MapApp extends PageBase {
    public mapObjects: MapObject[] | null = null;
    public openedMapObject: MapObject | null = null;
    public mapFilterRef: AppMapFilter | null = null;
    public mediaQueryService: MediaQueryService = MediaQueryService.getInstance();
    public objectReportService: ObjectReportService = ObjectReportService.getInstance();
    public mapReady: boolean = false;
    private mapFeaturesService = MapFeaturesService.getInstance();
    private apiService = ApiService.getInstance();
    private mapService: MapService = MapService.getInstance();
    private mapLayersService: MapLayersService = MapLayersService.getInstance();
    private filters: Filters = new Filters();

    constructor(options: any) {
        super(options);
    }

    public created(): void {
        this.whenPageReady.subscribe(() => {
            this.mapFilterRef = this.$refs.mapFilter as AppMapFilter;
            this.mapService.whenMapObjectSelected.subscribe((feature: Feature): void => {
                this.openMapObject(this.mapFeaturesService.mapObjectFromObjectFeature(feature));
            });
            this.mapService.whenMapObjectUnselected.subscribe((feature: Feature): void => {
                this.closeMapObject(this.mapFeaturesService.mapObjectFromObjectFeature(feature));
            });
            this.mapService.whenRouteSelected.subscribe((feature: Feature) => {
                this.openMapObject(this.mapFeaturesService.mapObjectFromRouteFeature(feature));
            });
            this.mapService.whenRouteUnselected.subscribe((feature: Feature) => {
                this.closeMapObject(this.mapFeaturesService.mapObjectFromRouteFeature(feature));
            });
            this.apiService.whenVisitedChanged.pipe(mergeMap(() => this.fetchAndRender())).subscribe();
            this.apiService.whenFavoritesChanged.pipe(mergeMap(() => this.fetchAndRender())).subscribe();
        });
    }

    public run(): void {
    }

    public infoContainerRef(): AppMapInfoContainer {
        return this.$refs['infoContainer'] as AppMapInfoContainer;
    }

    public isInfoContainerExpanded(): boolean {
        return this.infoContainerRef() ? this.infoContainerRef().isExpanded() : false;
    }

    public onInfoContentStateChange(isExpanded: boolean): void {
        this.updateMapPadding();
    }

    public initFiltersDone(filters: Filter[][]): void {
        this.filters.applyFilters(filters);
        of(null).pipe(
            mergeMap(() => this.mapService.renderFeatures([], [], this.filters)),
            mergeMap(() => this.mapFeaturesService.loadAllFeatures()),
            mergeMap(() => this.mapFeaturesService.loadAllRouteLayers()),
            mergeMap(() => this.mapService.renderFeatures([], [], this.filters)),
            mergeMap(() => this.updateMapPadding()),
            mergeMap(() => this.fetchAndRender()),
            mergeMap(() => this.fetchAndOpenMapRouteFromRoute()),
            mergeMap(() => this.fetchAndOpenMapObjectFromRoute()),
            map(() => this.mapReady = true),
        ).subscribe();
    }

    public applyFilters(filters: Filter[][]): void {
        this.filters.applyFilters(filters);
        this.fetchAndRender().subscribe();
    }

    public applySearch(filter: Filter): void {
        this.filters.applySearch(filter);
        this.fetchAndRender().subscribe();
    }

    public openMapObject(mapObject: MapObject): void {
        mapObject.type === MapObjectType.Object
            ? new MapObjectInRoute().update(mapObject.id)
            : new MapRouteInRoute().update(mapObject.id);
        this.openedMapObject = mapObject;
        if (!this.mediaQueryService.aboveMd) {
            this.infoContainerRef().collapse();
        }
    }

    public closeMapObject(mapObject: MapObject): void {
        this.openedMapObject = null;
        switch (mapObject.type) {
            case MapObjectType.Object:
                new MapObjectInRoute().remove();
                this.mapService.markFeatureUnselected(mapObject.mapFeature);
                break;
            case MapObjectType.Route:
                new MapRouteInRoute().remove();
                this.mapService.unselectRoute();
                break;
            default:
                throw new Error('Unknown map object type');
        }
    }

    public whenSelectMapObjectFromList(mapObject: MapObject): void {
        new MapRouteInRoute().remove();
        let observable: Observable<void>;
        switch (mapObject.type) {
            case MapObjectType.Object:
                observable = CustomObservable.empty().pipe(
                    mergeMap(() => this.mapService.unselectRoute()),
                    mergeMap(() => this.mapService.zoomToFeature(mapObject.mapFeature)),
                    map((): void => this.mapService.markFeatureSelected(mapObject.mapFeature)),
                );
                break;
            case MapObjectType.Route:
                observable = CustomObservable.empty().pipe(
                    mergeMap((): Observable<void> => this.mapService.selectRoute(mapObject.mapFeature)),
                );
                break;
            default:
                observable = CustomObservable.empty()
        }
        observable.subscribe();
        this.openMapObject(mapObject);
    }

    public whenMapObjectClose(mapObject: MapObject): void {
        this.closeMapObject(mapObject);
    }

    public hasSelectedFilters(): boolean {
        return this.filters.hasFilters();
    }

    public isFilterExpanded(): boolean {
        return !!this.mapFilterRef && this.mapFilterRef.isExpanded;
    }

    private fetchAndRender(): Observable<void> {
        const fetchAndRenderFeatures: Observable<void> = forkJoin([
            this.mapFeaturesService.objectFeatures(this.filters),
            this.mapFeaturesService.routeFeatures(this.filters),
        ]).pipe(
            mergeMap((responses: Feature[][]) => {
                const objects: MapObject[] = responses[0].map((feature: Feature) => this.mapFeaturesService.mapObjectFromObjectFeature(feature));
                const routes: MapObject[] = responses[1].map((feature: Feature) => this.mapFeaturesService.mapObjectFromRouteFeature(feature));
                this.mapObjects = objects.concat(routes);
                return this.mapService.renderFeatures(responses[0] || [], responses[1] || [], this.filters);
            }),
        );
        const fetchAndRenderLayers: Observable<void> = this.mapLayersService.layerVisibilities(this.filters).pipe(
            mergeMap((layerVisibilities: LayerVisibility[]) => {
                return this.mapService.changeLayerVisibilities(layerVisibilities);
            })
        );
        return CustomObservable.empty().pipe(
            mergeMap(() => fetchAndRenderFeatures),
            mergeMap(() => fetchAndRenderLayers),
        );
    }

    private fetchAndOpenMapRouteFromRoute(): Observable<void> {
        const mapRouteId: string = new MapRouteInRoute().value();
        return mapRouteId
            ? this.mapRouteById(mapRouteId).pipe(
                mergeMap((mapRoute: MapRoute) => {
                    return this.mapService.selectRoute(mapRoute.mapFeature);
                }),
                catchError(() => CustomObservable.empty()),
            )
            : CustomObservable.empty();
    }

    private fetchAndOpenMapObjectFromRoute(): Observable<void> {
        const mapObjectId: string = new MapObjectInRoute().value();
        const mapRouteId: string = new MapRouteInRoute().value();
        return mapObjectId
            ? this.mapObjectById(mapObjectId).pipe(
                mergeMap((mapObject: MapObject) => {
                    this.mapService.markFeatureSelected(mapObject.mapFeature);
                    this.openMapObject(mapObject);
                    return !mapRouteId
                        ? this.mapService.zoomToFeature(mapObject.mapFeature)
                        : CustomObservable.empty();
                }),
                catchError(() => CustomObservable.empty()),
            )
            : CustomObservable.empty();
    }

    private mapRouteById(mapRouteId: string): Observable<MapRoute> {
        return this.mapFeaturesService.routeFeature(mapRouteId).pipe(
            map((feature: Feature) => {
                return this.mapFeaturesService.mapRouteFromFeature(feature);
            })
        );
    }

    private mapObjectById(mapObjectId: string): Observable<MapObject> {
        return this.mapFeaturesService.objectFeature(mapObjectId).pipe(
            map((feature: Feature) => {
                return this.mapFeaturesService.mapObjectFromObjectFeature(feature);
            })
        );
    }

    private updateMapPadding(): Observable<void> {
        return this.mapService.changePadding(this.infoContainerRef().width());
    }
}
